import { ColumnDef } from '@tanstack/react-table'
import Typography from '../../atoms/Typography'
import Badge from '../../atoms/Badge'
import IdCell from '../../atoms/IdCell'
import { formatBytes } from 'src/enviroment/constants/constants'
import { getStatusIcon } from './getStatusIcon'
import { IconCopy } from '@tabler/icons-react'
import { LayerItem, LayerUploadStatus } from 'src/types/layer'
import { enqueueSnackbar } from 'notistack'
import DeleteDialog from 'src/components/organisms/dialogs/DeleteDialog'
import UploadJsonStylesDialog from 'src/components/organisms/dialogs/UploadJsonStylesDialog'
import UploadJsonComponentsDialog from 'src/components/organisms/dialogs/UploadJsonComponentsDialog'
import EditableNameCell from './EditableNameCell'
import EditableOrderCell from './EditableOrderCell'
import LayerVisibilityToggle from '../../atoms/LayerVisibilityToggle'
import EditLayerDescriptionDialog from 'src/components/organisms/dialogs/EditLayerDescriptionDialog'
import EditLayerDialog from 'src/components/organisms/dialogs/EditLayerDialog'
import ApproveLayerDialog from 'src/components/organisms/dialogs/ApproveLayerDialog'
import LayerTierSelect from 'src/components/organisms/LayerTierSelect'
import LayerClipOutputSelect from 'src/components/organisms/LayerClipOutputSelect'

const AccessBadge = ({ layer }: { layer: LayerItem }) => {
  if (layer.isPublic) {
    return <Badge>Public</Badge>
  }
  const count = layer.allowedTenants.length
  if (count === 0) {
    return <Badge variant="danger">Private</Badge>
  }
  return (
    <Badge variant="accent" className="whitespace-nowrap">
      {count} tenants
    </Badge>
  )
}

export const useColumns = (
  // TO DO
  // handleDownload: (layer: LayerItem) => void,
  // handleRetry: (id: string) => void,
  handleDelete: (id: string) => void,
  handleUpdateName: (id: string, newName: string) => Promise<void>,
  handleUpdateOrder: (id: string, orderNumber: number | null) => Promise<void>,
) => {
  const copyToClipboard = async (text: string, message = 'Copied to clipboard') => {
    await navigator.clipboard.writeText(text)
    enqueueSnackbar(message, { variant: 'success' })
  }

  const columns: ColumnDef<LayerItem>[] = [
    {
      accessorKey: 'id',
      header: 'ID',
      cell: ({ getValue }) => {
        const id = getValue<string>()
        return (
          <div className="flex items-center">
            <IdCell id={id} />
          </div>
        )
      },
    },
    {
      accessorKey: 'orderNumber',
      header: 'Order',
      cell: ({ row }) => <EditableOrderCell rowData={row.original} onUpdate={handleUpdateOrder} />,
    },
    {
      accessorKey: 'fileName',
      header: 'File Name',
      // A shadow, not a border: collapsed table borders don't travel with a sticky cell.
      meta: {
        headerClassName: 'sticky left-0 z-10 shadow-[1px_0_0_0_theme(colors.gray.700)]',
        cellClassName: 'sticky left-0 z-10 bg-gray-800 shadow-[1px_0_0_0_theme(colors.gray.700)]',
      },
      cell: ({ row, getValue }) => (
        <EditableNameCell initialValue={getValue<string>()} rowData={row.original} onUpdate={handleUpdateName} />
      ),
    },
    {
      accessorKey: 'sizeBytes',
      header: 'Size',
      cell: ({ getValue }) => (
        <Typography variant="base" className="text-white">
          {formatBytes(getValue<number>())}
        </Typography>
      ),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => {
        const { status, id, fileName } = row.original
        const badge = (
          <div className="flex items-center gap-2">
            {getStatusIcon(status)}
            <Typography variant="base" className="lowercase text-white">
              {status}
            </Typography>
          </div>
        )

        // A pending-review layer's status doubles as the "mark as completed" action.
        if (status === LayerUploadStatus.NEEDS_REVIEW) {
          return (
            <ApproveLayerDialog fileId={id} fileName={fileName}>
              <div className="rounded-md px-1 transition-colors hover:bg-gray-700" title="Review & approve">
                {badge}
              </div>
            </ApproveLayerDialog>
          )
        }

        return badge
      },
    },
    {
      id: 'dataset',
      header: 'Dataset',
      cell: ({ row }) => {
        const name = row.original.dataset?.name
        return name ? <Badge variant="accent">{name}</Badge> : <Badge>None</Badge>
      },
    },
    {
      accessorKey: 'country',
      header: 'Country',
      cell: ({ getValue }) => {
        const country = getValue<string | null>()
        return country ? <Badge variant="accent">{country}</Badge> : <Badge>Global</Badge>
      },
    },
    {
      id: 'access',
      header: 'Access',
      cell: ({ row }) => <AccessBadge layer={row.original} />,
    },
    {
      accessorKey: 'tier',
      header: 'Tier',
      cell: ({ row }) => (
        <LayerTierSelect
          fileId={row.original.id}
          value={row.original.tier}
          className="w-[140px] border-gray-700 bg-transparent text-gray-100"
        />
      ),
    },
    {
      accessorKey: 'clipOutputType',
      header: 'Clip output',
      cell: ({ row }) => (
        <LayerClipOutputSelect
          layer={row.original}
          className="w-[120px] border-gray-700 bg-transparent text-gray-100"
        />
      ),
    },
    {
      accessorKey: 'blobUrl',
      header: 'URL',
      cell: ({ getValue }) => {
        const url = getValue<string>()
        return (
          <button
            onClick={() => copyToClipboard(url, 'URL copied to clipboard')}
            className="text-gray-400 transition-colors hover:text-blue-500"
            title="Copy Path"
          >
            <IconCopy className="h-5 w-5" />
          </button>
        )
      },
    },
    {
      accessorKey: 'createdAt',
      header: 'Upload Date',
      cell: ({ getValue }) => (
        <Typography variant="base" className="text-white">
          {new Date(getValue<string>()).toLocaleDateString()}
        </Typography>
      ),
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => {
        const { status, id, widgets, style, globallyVisibleStatus, description } = row.original
        // Only a still-uploading layer has no file to act on yet; every other status gets the full toolset.
        const isReady = status !== LayerUploadStatus.UPLOADING

        return (
          <div className="flex items-center gap-2">
            {isReady && <EditLayerDialog layer={row.original} />}
            {isReady && <LayerVisibilityToggle id={id} isVisible={globallyVisibleStatus} />}
            {isReady && <EditLayerDescriptionDialog id={id} description={description} />}
            {isReady && <UploadJsonStylesDialog id={id} json={style} />}
            {isReady && <UploadJsonComponentsDialog id={id} json={widgets} />}
            <DeleteDialog
              title="Delete File"
              description={`You're about to delete the ${row.original.fileName} file. Are you sure you want to proceed?`}
              onDelete={async () => handleDelete(id)}
            />
          </div>
        )
      },
    },
  ]

  return columns
}
